// Vendor imports
const _ = require('lodash')
const React = require('react')
const PropTypes = require('prop-types')
const classNames = require('classnames')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const usePrevious = require('client-v2/common/hooks/use-previous')
const { ModalDepthModifier } = require('client-v2/common/contexts/modal-depth')
// Container imports
const ArticleProfileV2DetailCard = require('../components-v2/detail-card-smart')
const ArticleProfileV2Related = require('../components-v2/related-smart')

// BEM constants
const bem = BemEntity('ArticleProfileModalV2')

// Swipe constants
const MAX_SWIPE_WIDTH_FACTOR = 0.9
const MIN_SWIPE_WIDTH_FACTOR = 0.25
const BROWSER_BACK_SWIPE_RANGE = 40 // px
const KEY_SCROLL_AMOUNT = 100 // px
const SwipeDirectionEnum = {
  NONE: 'none',
  LEFT: 'left',
  RIGHT: 'right'
}

// Keycode constants
const CLOSE_MODAL_KEYCODE = 27 // Escape
const SWIPE_LEFT_KEYCODE = 37 // Left Arrow
const SWIPE_RIGHT_KEYCODE = 39 // Right Arrow
const SCROLL_UP_KEYCODE = 38 // Up Arrow
const SCROLL_DOWN_KEYCODE = 40 // Down Arrow
const captureKeyCodes = [
  CLOSE_MODAL_KEYCODE,
  SWIPE_LEFT_KEYCODE,
  SWIPE_RIGHT_KEYCODE,
  SCROLL_UP_KEYCODE,
  SCROLL_DOWN_KEYCODE
]

const DEBOUNCE_DELAY = 300 // ms
const DEBOUNCE_OPTIONS = { leading: true, trailing: false }

function ArticleProfileModal(props) {

  const prevProps = usePrevious(props)

  React.useEffect(() => {
    if (typeof window === 'object') {
      window.addEventListener('keydown', onKeyDown)
      document.addEventListener('touchstart', onSwipeStart)
      document.addEventListener('touchmove', onSwipeMove)
      document.addEventListener('touchend', onSwipeEnd)
    }

    if (prevProps){
      const indexDiff = prevProps.currentIndex - props.currentIndex
      const depthDiff = prevProps.modalDepth - props.modalDepth
      const countDiff = prevProps.slideCount - props.slideCount

      if (!props.slideCount) {
        // Close the modal if there are no slides to show
        props.onClose()
      } else if (countDiff >= 1 && !depthDiff) {
        // Refresh the current slide if slides have been removed
        props.onSlideRefresh()
      } else if (Math.abs(indexDiff) > 1 || depthDiff) {
        // Prevent sliding behavior when showing related article profile modal
        setSwipeDirection(SwipeDirectionEnum.NONE)
      } else if (indexDiff > 0) {
        setSwipeDirection(SwipeDirectionEnum.LEFT)
      } else if (indexDiff < 0) {
        setSwipeDirection(SwipeDirectionEnum.RIGHT)
      }
    }

    return () => {
      if (typeof window === 'object') {
        window.removeEventListener('keydown', onKeyDown)
        document.removeEventListener('touchstart', onSwipeStart)
        document.removeEventListener('touchmove', onSwipeMove)
        document.removeEventListener('touchend', onSwipeEnd)
      }
    }
  })

  const onSwipeLeft = _.debounce(props.onSwipeLeft, DEBOUNCE_DELAY, DEBOUNCE_OPTIONS)
  const onSwipeRight = _.debounce(props.onSwipeRight, DEBOUNCE_DELAY, DEBOUNCE_OPTIONS)

  const [isSwiping, setSwiping] = React.useState(false)
  const [swipeDirection, setSwipeDirection] = React.useState(SwipeDirectionEnum.NONE)
  const [swipeWidth, setSwipeWidth] = React.useState(0)
  const [swipe, setSwipe] = React.useState(null)

  const currentSlideRef = React.useRef(null)

  const scrollCurrentSlide = (scrollAmount) => {
    const slideEl = currentSlideRef.current
    if (slideEl && scrollAmount) {
      slideEl.scrollTop += scrollAmount
    }
  }

  // Swipe handling methods

  const onSwipeStart = (event) => {
    const currentSlideEl = currentSlideRef.current
    if (!props.isVisible || !currentSlideEl || isSwiping) {
      return
    }
    const { width } = currentSlideEl.getBoundingClientRect()

    setSwipe({
      touchStartX: _.get(event, 'touches[0].pageX'),
      touchStartY: _.get(event, 'touches[0].pageY'),
      maxWidth: width * MAX_SWIPE_WIDTH_FACTOR
    })
    setSwiping(true)
    setSwipeDirection(SwipeDirectionEnum.NONE)
    setSwipeWidth(0)
  }

  const onSwipeMove = (event) => {
    if (!isSwiping) {
      return
    }
    // Determine the delta of the movement
    const moveX = _.get(event, 'touches[0].pageX')
    const moveY = _.get(event, 'touches[0].pageY')
    const diffX = swipe.touchStartX - moveX
    const diffY = swipe.touchStartY - moveY
    // Cancel the swipe if there's a more-important gesture
    const isVerticalSwipe = Math.abs(diffY) > Math.abs(diffX)
    const isBrowserBackSwipe = swipe.touchStartX < BROWSER_BACK_SWIPE_RANGE
    if (isVerticalSwipe || isBrowserBackSwipe) {
      setSwipeWidth(0)
      return
    }
    // Determine which slide to swipe in and how far
    let swipeWidth = 0
    if (diffX < 0) {
      swipeWidth = Math.min(-diffX, swipe.maxWidth)
    } else if (diffX > 0) {
      swipeWidth = Math.max(-diffX, -swipe.maxWidth)
    }
    // Peek the next/previous slide in by the swipe amount
    setSwipeWidth(swipeWidth)
  }

  const onSwipeEnd = () => {
    if (!isSwiping) {
      return
    }
    const threshold = swipe.maxWidth * MIN_SWIPE_WIDTH_FACTOR
    // Clear swipe state
    setSwipe(null)
    setSwiping(false)
    setSwipeWidth(0)

    // Determine if swipe triggered index update
    if (Math.abs(swipeWidth) > threshold) {
      if (swipeWidth > 0) {
        onSwipeLeft()
      }
      if (swipeWidth < 0) {
        onSwipeRight()
      }
    }
  }

  // Other event handling methods

  const onKeyDown = (event) => {
    // Do nothing if the modal is not currently being shown
    if (!props.isVisible) {
      return
    }
    const { keyCode, target: { tagName } } = event
    // Ignore keyCodes that the modal is not interested in
    if (!captureKeyCodes.includes(keyCode)) {
      return
    }
    // Ensure text-capturing inputs keep doing their thing
    if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
      return
    }
    event.preventDefault()
    switch (keyCode) {
      case CLOSE_MODAL_KEYCODE:
        props.onClose()
        break
      case SWIPE_LEFT_KEYCODE:
        onSwipeLeft()
        break
      case SWIPE_RIGHT_KEYCODE:
        onSwipeRight()
        break
      case SCROLL_UP_KEYCODE:
        scrollCurrentSlide(-KEY_SCROLL_AMOUNT)
        break
      case SCROLL_DOWN_KEYCODE:
        scrollCurrentSlide(KEY_SCROLL_AMOUNT)
        break
    }
  }

  if (!props.isVisible) {
    return null
  }

  return (
    tag('div', {
      className: classNames(
        props.className,
        bem.block()
      )
    },
      tag(ModalDepthModifier),
      tag(Backdrop, props),
      tag(Content, {
        ...props,
        swipeWidth,
        isSwiping,
        swipeDirection,
        currentSlideRef,
        onSwipeLeft,
        onSwipeRight
      }),
      tag(CloseButton, props)
    )
  )
}

ArticleProfileModal.propTypes = {
  // Flags
  isVisible: PropTypes.bool.isRequired,
  // Values
  className: PropTypes.string,
  currentIndex: PropTypes.number.isRequired,
  modalDepth: PropTypes.number.isRequired,
  slideCount: PropTypes.number.isRequired,
  // Callbacks
  onClose: PropTypes.func.isRequired,
  onSlideRefresh: PropTypes.func.isRequired,
  onSwipeLeft: PropTypes.func.isRequired,
  onSwipeRight: PropTypes.func.isRequired
}

module.exports = ArticleProfileModal

// BEM Block entity Element components

function Backdrop(props) {
  return (
    tag('div', {
      className: bem.element('backdrop'),
      onClick: props.onClose
    })
  )
}

function CloseButton(props) {
  return (
    tag('div', {
      className: bem.element('close-container')
    },
      tag('button', {
        className: bem.element('close-button'),
        onClick: props.onClose
      })
    )
  )
}

function SwipeLeftButton(props) {
  if (props.currentIndex <= 0) {
    return null
  }
  return (
    tag('button', {
      type: 'button',
      className: bem.element('swipe-button', 'left'),
      onClick: props.onSwipeLeft
    })
  )
}

function SwipeRightButton(props) {
  const { currentIndex, slideCount } = props
  if (currentIndex + 1 >= slideCount) {
    return null
  }
  return (
    tag('button', {
      type: 'button',
      className: bem.element('swipe-button', 'right'),
      onClick: props.onSwipeRight
    })
  )
}

function Content(props) {

  const onCloseCapture = (event) => {
    const targetClassNames = event.target.className.split(' ')
    const captureClassName = bem.element('slide-outer')
    if (targetClassNames.includes(captureClassName)) {
      event.preventDefault()
      event.stopPropagation()
      props.onClose()
    }
  }

  const renderSlide = (slideIndexOffset = 0) => {
    const { currentIndex, modalDepth, slideCount, currentSlideRef } = props
    const slideIndex = currentIndex + slideIndexOffset
    const detailCardRef = React.useRef(null)

    const isCuratorVisible = () => {
      const currentSlideEl = currentSlideRef.current
      const detailCardEl = detailCardRef.current
      if (!currentSlideEl || !detailCardEl) {
        return true
      }
      return detailCardEl.clientHeight > currentSlideEl.scrollTop
    }

    if (slideIndex < 0 || slideIndex >= slideCount) {
      return null
    }
    return (
      tag('div', {
        // There is no unique key; falling back to index
        key: `${modalDepth}:${slideIndex}`,
        ref: slideIndexOffset ? null : currentSlideRef,
        // Outer is responsible for the full-window scrolling
        className: bem.element('slide-outer', {
          previous: slideIndexOffset < 0,
          current: slideIndexOffset == 0,
          next: slideIndexOffset > 0
        }),
        onClickCapture: onCloseCapture
      },
        tag('div', {
          // Inner is responsible for centered content
          className: bem.element('slide-inner')
        },
          tag('div', {
            className: bem.element('detail-card-container'),
            ref: detailCardRef
          },
            tag(ArticleProfileV2DetailCard, {
              slideIndex,
              isCuratorVisible: isCuratorVisible()
            })
          ),
          tag(ArticleProfileV2Related, { slideIndex }),
          tag('div', {
            className: bem.element('buttons-container')
          },
            tag(SwipeLeftButton, props),
            tag(SwipeRightButton, props)
          )
        )
      )
    )
  }

  const { swipeWidth, isSwiping, swipeDirection } = props
  const transform = `translateX(${swipeWidth}px)`

  return (
    tag('div', {
      className: bem.element('content', {
        swiping: isSwiping,
        direction: swipeDirection
      }),
      style: { transform }
    },
      [
        renderSlide(-1),
        renderSlide(0),
        renderSlide(+1)
      ]
    )
  )
}
